import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Circle, MoreVertical, ShieldOff, Flag, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, getToken } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

function timeFmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  if (same) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [otherUser, setOtherUser] = useState(location.state?.otherUser || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [reportCategory, setReportCategory] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // Load messages + other user (parallelized; uses single-match endpoint when no router state)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [msgData, matchData] = await Promise.all([
          api(`/matches/${matchId}/messages`),
          otherUser ? Promise.resolve(null) : api(`/matches/${matchId}`),
        ]);
        if (cancelled) return;
        setMessages(msgData.messages || []);
        if (!otherUser && matchData?.user) {
          setOtherUser(matchData.user);
        }
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to load chat');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // WebSocket
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws') + `/api/ws/${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'new_message' && data.match_id === matchId) {
          setMessages((m) => [...m, data.message]);
          // Auto-mark as read
          ws.send(JSON.stringify({ action: 'read', match_id: matchId }));
        } else if (data.type === 'typing' && data.match_id === matchId && data.user_id !== user?.id) {
          setOtherTyping(data.is_typing);
        } else if (data.type === 'messages_read' && data.match_id === matchId) {
          setMessages((msgs) =>
            msgs.map((m) => (m.sender_id === user?.id ? { ...m, read: true } : m))
          );
        }
      } catch {}
    };

    return () => {
      ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, user?.id]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, otherTyping]);

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    const myText = text.trim();
    setText('');

    // Try WebSocket first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'message', match_id: matchId, text: myText }));
      // Optimistic UI: add a temp message immediately (server will broadcast back, may dedupe)
      const optimistic = {
        id: `tmp-${Date.now()}`,
        match_id: matchId,
        sender_id: user.id,
        text: myText,
        read: false,
        created_at: new Date().toISOString(),
        _optimistic: true,
      };
      setMessages((m) => [...m, optimistic]);
      setSending(false);
    } else {
      // Fallback to REST
      try {
        const data = await api(`/matches/${matchId}/messages`, {
          method: 'POST',
          body: JSON.stringify({ text: myText }),
        });
        setMessages((m) => [...m, data.message]);
      } catch (err) {
        toast.error(err.message || 'Failed to send');
        setText(myText);
      } finally {
        setSending(false);
      }
    }
  }

  function handleTyping(e) {
    setText(e.target.value);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'typing', match_id: matchId, is_typing: true }));
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        wsRef.current?.send(JSON.stringify({ action: 'typing', match_id: matchId, is_typing: false }));
      }, 2000);
    }
  }

  async function handleBlock() {
    if (!otherUser?.id) return;
    if (!window.confirm(`Block ${otherUser.name}? You'll no longer match or see each other.`)) return;
    try {
      await api(`/users/block/${otherUser.id}`, { method: 'POST' });
      toast.success('User blocked');
      navigate('/app/matches');
    } catch (err) {
      toast.error(err.message || 'Failed to block');
    }
  }

  async function handleReport(e) {
    e.preventDefault();
    if (!otherUser?.id) return;
    if (!reportCategory) return toast.error('Please choose a category');
    setReportSubmitting(true);
    try {
      await api('/users/report', {
        method: 'POST',
        body: JSON.stringify({
          reported_user_id: otherUser.id,
          category: reportCategory,
          description: reportDescription.trim() || null,
        }),
      });
      toast.success('Report submitted. The user has been blocked.');
      setReportModal(false);
      navigate('/app/matches');
    } catch (err) {
      toast.error(err.message || 'Failed to report');
    } finally {
      setReportSubmitting(false);
    }
  }

  // Dedupe optimistic messages when server replies arrive
  const visibleMessages = (() => {
    const seen = new Set();
    const out = [];
    for (const m of messages) {
      const k = `${m.sender_id}:${m.text}:${m.created_at?.slice(0, 16)}`;
      if (!m._optimistic && seen.has(k)) continue;
      seen.add(k);
      out.push(m);
    }
    // Remove optimistic if a real one with same text+sender exists
    return out.filter((m) => {
      if (!m._optimistic) return true;
      return !out.some(
        (x) => !x._optimistic && x.sender_id === m.sender_id && x.text === m.text
      );
    });
  })();

  return (
    <div className="max-w-3xl mx-auto h-[calc(100vh-64px)] md:h-[calc(100vh-64px)] flex flex-col bg-white" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F4F1DE] bg-white">
        <button
          onClick={() => navigate('/app/matches')}
          data-testid="chat-back-button"
          className="w-9 h-9 rounded-full hover:bg-[#F4F1DE] flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5 text-[#2B2D42]" />
        </button>
        {otherUser && (
          <Link
            to={`/app/user/${otherUser.id}`}
            className="flex items-center gap-3 flex-1 min-w-0"
            data-testid="chat-header-user-link"
          >
            <div className="w-10 h-10 rounded-full overflow-hidden bg-[#FFE5DD] flex-shrink-0">
              {otherUser.photos?.[0] ? (
                <img src={otherUser.photos[0]} alt={otherUser.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-base font-bold text-[#C8553D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {otherUser.name?.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#2B2D42] truncate">{otherUser.name}</p>
              <p className="text-xs text-[#6D6875] flex items-center gap-1">
                <Circle className={`w-2 h-2 ${wsConnected ? 'fill-[#2A9D8F] text-[#2A9D8F]' : 'fill-[#B5B5B5] text-[#B5B5B5]'}`} />
                {wsConnected ? 'Connected' : 'Connecting...'}
              </p>
            </div>
          </Link>
        )}

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="chat-menu-button"
            className="w-9 h-9 rounded-full hover:bg-[#F4F1DE] flex items-center justify-center"
          >
            <MoreVertical className="w-5 h-5 text-[#2B2D42]" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div
                data-testid="chat-menu-dropdown"
                className="absolute right-0 top-full mt-1 w-48 bg-white border border-[#F4F1DE] rounded-xl shadow-xl py-1 z-20"
              >
                <button
                  onClick={() => { setMenuOpen(false); setReportModal(true); }}
                  data-testid="chat-menu-report"
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#FFE5DD]/40 text-sm text-[#2B2D42]"
                >
                  <Flag className="w-4 h-4 text-[#E76F51]" /> Report
                </button>
                <button
                  onClick={() => { setMenuOpen(false); handleBlock(); }}
                  data-testid="chat-menu-block"
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#FFE5DD]/40 text-sm text-[#2B2D42]"
                >
                  <ShieldOff className="w-4 h-4 text-[#E76F51]" /> Block
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-[#FFFCF9]" data-testid="chat-messages">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-[#C8553D]" />
          </div>
        ) : visibleMessages.length === 0 ? (
          <div className="text-center text-sm text-[#6D6875] mt-8" data-testid="chat-empty">
            <p>No messages yet.</p>
            <p className="mt-1">Send the first message!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleMessages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                      mine
                        ? 'bg-[#C8553D] text-white rounded-br-md'
                        : 'bg-white border border-[#F4F1DE] text-[#2B2D42] rounded-bl-md'
                    } ${m._optimistic ? 'opacity-60' : ''}`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                    <p className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-[#B5B5B5]'} text-right`}>
                      {timeFmt(m.created_at)}
                      {mine && <span className="ml-1">{m.read ? '✓✓' : '✓'}</span>}
                    </p>
                  </div>
                </div>
              );
            })}
            {otherTyping && (
              <div className="flex justify-start" data-testid="typing-indicator">
                <div className="bg-white border border-[#F4F1DE] rounded-2xl rounded-bl-md px-4 py-3 inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B5B5B5] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B5B5B5] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B5B5B5] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="border-t border-[#F4F1DE] bg-white px-3 py-3 flex items-center gap-2" data-testid="chat-composer">
        <input
          type="text"
          value={text}
          onChange={handleTyping}
          placeholder="Type a message..."
          data-testid="chat-input"
          className="flex-1 bg-[#FFFCF9] border border-[#F4F1DE] rounded-full px-4 py-3 outline-none focus:border-[#E07A5F] text-[#2B2D42] placeholder-[#B5B5B5]"
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          data-testid="chat-send-button"
          className="w-11 h-11 rounded-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-40 text-white flex items-center justify-center flex-shrink-0 transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>

      {/* Report Modal */}
      {reportModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="report-modal" onClick={() => setReportModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>Report user</h3>
              <button onClick={() => setReportModal(false)} data-testid="report-close-button" className="text-[#6D6875]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#6D6875] mb-4">Reporting will also block this user. Our team reviews reports within 24h.</p>
            <form onSubmit={handleReport} data-testid="report-form" className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6875] mb-1.5">Why are you reporting?</label>
                <select
                  value={reportCategory}
                  onChange={(e) => setReportCategory(e.target.value)}
                  data-testid="report-category-select"
                  className="w-full bg-white border border-[#F4F1DE] rounded-lg px-3 py-2 outline-none focus:border-[#E07A5F] text-[#2B2D42]"
                >
                  <option value="">Choose a reason...</option>
                  <option>Fake profile</option>
                  <option>Inappropriate photos</option>
                  <option>Harassment</option>
                  <option>Spam/scam</option>
                  <option>Underage user</option>
                  <option>Offensive content</option>
                  <option>Impersonation</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6875] mb-1.5">Additional details (optional)</label>
                <textarea
                  rows={3}
                  maxLength={500}
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  data-testid="report-description-input"
                  placeholder="What happened?"
                  className="w-full bg-white border border-[#F4F1DE] rounded-lg px-3 py-2 outline-none focus:border-[#E07A5F] text-[#2B2D42] text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={reportSubmitting || !reportCategory}
                data-testid="report-submit-button"
                className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-3 rounded-full flex items-center justify-center gap-2"
              >
                {reportSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                Submit Report
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
